import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Umbrella, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LEAVE_TYPES = ['vacation', 'sick', 'personal', 'bereavement', 'unpaid'];

export default function MyLeavePage() {
  const [balance, setBalance] = useState({});
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/api/employee-portal/leave/balance`),
        axios.get(`${API}/api/employee-portal/leave/requests`),
      ]);
      setBalance(b.data || {});
      setRequests(r.data.requests || r.data || []);
    } catch(e){ if(e?.response?.status !== 403) toast.error('Failed to load leave data'); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (!form.start_date || !form.end_date) { toast.error('Pick start & end dates'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/employee-portal/leave/request`, form);
      toast.success('Leave request submitted — your manager will review it.');
      setForm({ leave_type: 'vacation', start_date: '', end_date: '', reason: '' });
      load();
    } catch(e){ if(e?.response?.status !== 403) toast.error(e.response?.data?.detail || 'Failed to submit request'); } finally { setBusy(false); }
  };

  const statusColor = (s) => ({ approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-red-100 text-red-800', pending: 'bg-yellow-100 text-yellow-800' }[s] || 'bg-gray-100 text-gray-700');

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-leave-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Time-off & Leave</h1>
          <p className="text-gray-600 mt-1">Request time off and track your balance.</p>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          {LEAVE_TYPES.slice(0, 4).map(t => (
            <Card key={t}><CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-gray-500">{t}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{balance[t] ?? 0} <span className="text-sm text-gray-500 font-normal">days</span></div>
            </CardContent></Card>
          ))}
        </div>

        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Umbrella className="h-5 w-5 text-purple-500" /> Request Leave</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Leave type</Label>
                <select data-testid="leave-type" value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}
                  className="w-full h-10 border border-gray-300 rounded-md px-3">
                  {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><Label>Start</Label><Input data-testid="leave-start" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>End</Label><Input data-testid="leave-end" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div><Label>Reason (optional)</Label><Textarea data-testid="leave-reason" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2} /></div>
            <Button data-testid="leave-submit" onClick={submit} disabled={busy} className="bg-purple-500 hover:bg-purple-600">
              <Send className="h-4 w-4 mr-2" /> {busy ? 'Submitting…' : 'Submit request'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>My requests</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-gray-500">Loading…</div> :
             requests.length === 0 ? <div className="text-gray-500 italic">No requests yet.</div> :
             <div className="divide-y divide-gray-100">
               {requests.map((r, i) => (
                 <div key={i} className="py-3 flex items-center justify-between">
                   <div>
                     <div className="font-medium text-gray-900 capitalize">{r.leave_type}</div>
                     <div className="text-xs text-gray-500">{r.start_date} → {r.end_date} · {r.days || ''}d</div>
                     {r.reason && <div className="text-xs text-gray-600 mt-0.5 italic">&ldquo;{r.reason}&rdquo;</div>}
                   </div>
                   <Badge className={statusColor(r.status)}>{r.status}</Badge>
                 </div>
               ))}
             </div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
